import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './App.css';

// Components
import Login from './pages/Login';
import OidcCallback from './pages/OidcCallback';
import Dashboard from './pages/Dashboard';
import AddProject from './pages/AddProject';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import AddTestCyclePage from './pages/AddTestCycle';
import UserManagement from './pages/UserManagement';
import UserRegistration from './components/user/UserRegistration';
import PasswordChange from './components/PasswordChange';
import LandingPage from './pages/LandingPage';
import Analytics from './pages/Analytics';
import Resources from './pages/Resources';

// Services and Types
import { getCurrentUser, logoutUser } from './services/userService';
import { User } from './types/user';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, onLogout }) => {
  const user = getCurrentUser();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return (
    <>
      {children}
    </>
  );
};

// Renders OIDC callback handler when the provider redirects back to the root
// URI with authorization code/state or with an OIDC error; otherwise shows the landing page.
const HomeRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const hasOidcParams = searchParams.has('code') && searchParams.has('state');
  const hasOidcError = searchParams.has('error');

  if (hasOidcParams || hasOidcError) {
    return <OidcCallback />;
  }

  return <LandingPage />;
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPasswordChange, setShowPasswordChange] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Check if user needs to change password
  const checkPasswordChangeRequired = () => {
    const user = getCurrentUser();
    if (user && user.passwordChangeRequired) {
      setCurrentUser(user);
      setShowPasswordChange(true);
      return true;
    }
    return false;
  };

  // Redirect authenticated users from / or /login to /dashboard
  useEffect(() => {
    const user = getCurrentUser();
    if (user && (location.pathname === '/' || location.pathname === '/login') && !user.passwordChangeRequired) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogin = () => {

    // Check if password change is required
    if (!checkPasswordChangeRequired()) {
      navigate('/dashboard');
    } else {
      // Don't navigate - password change component will show as overlay
    }
  };

  const handlePasswordChangeComplete = () => {
    const user = getCurrentUser();
    if (user) {
      user.passwordChangeRequired = false;
      localStorage.setItem('cqi_user', JSON.stringify(user));
    }
    setShowPasswordChange(false);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const userForPasswordSetup = getCurrentUser();
  const isOidcInitialSetup = userForPasswordSetup?.authSource === 'oidc' && userForPasswordSetup?.passwordChangeRequired;

  return (
    <>
      {showPasswordChange && (
        <PasswordChange
          onComplete={handlePasswordChangeComplete}
          isInitialSetup={isOidcInitialSetup}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={<HomeRoute />}
        />
        <Route
          path="/login"
          element={<Login onLogin={handleLogin} />}
        />
        <Route
          path="/register"
          element={<UserRegistration />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <Dashboard onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-project"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <AddProject onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/ongoing"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <ProjectList status="ongoing" onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/completed"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <ProjectList status="completed" onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/learning-forum"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <ProjectList status="archived" onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/drafts"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <ProjectList status="draft" onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:id"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <ProjectDetail onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-test-cycle"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <AddTestCyclePage onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <UserManagement onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <Analytics onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/resources"
          element={
            <ProtectedRoute onLogout={handleLogout}>
              <Resources onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;